import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PromoBanner from '../components/PromoBanner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Calculator,
  Search,
  MessageSquare,
  Wrench,
  FileText,
  TrendingUp,
  Clock,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle2,
  Star,
  Building2,
  Lock,
  Loader2,
  FolderKanban,
  Users,
  Megaphone,
  HardHat,
  Store,
  BarChart3,
  FileSignature,
  Landmark,
  Rocket,
  Activity,
  Presentation,
  Home as HomeIcon,
} from 'lucide-react';
import { mockTestimonials, mockPricingPlans, formatCurrency } from '../data/mockData';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CountdownTimer = () => {
  return (
    <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl" data-testid="launch-badge">
      <Rocket className="w-5 h-5 text-green-500" />
      <span className="text-sm font-semibold text-green-600 dark:text-green-400">AI-Powered Property Analysis</span>
    </div>
  );
};

const LiveStatsBar = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/stats/live`)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  const items = stats ? [
    { label: 'Users', value: stats.users, icon: Users },
    { label: 'Deals Analyzed', value: stats.deals_analyzed, icon: Calculator },
    { label: 'Leads Tracked', value: stats.leads_tracked, icon: Activity },
    { label: 'Docs Signed', value: stats.documents_signed, icon: FileSignature },
  ] : [];

  if (!stats) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="live-stats-bar">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl px-4 py-3" data-testid={`live-stat-${i}`}>
          <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center shrink-0">
            <item.icon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-white leading-none">{item.value.toLocaleString()}</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">{item.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await login(adminEmail, adminPassword);
      if (result.user?.isAdmin) {
        toast.success('Welcome back, Admin!');
        setShowAdminLogin(false);
        navigate('/admin');
      } else {
        toast.error('This account does not have admin privileges');
      }
    } catch (error) {
      toast.error(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Calculator,
      title: t('home.dealAnalyzer'),
      description: 'Calculate ARV, MAO, ROI, and cash flow for any investment strategy in seconds.',
      link: '/analyzer'
    },
    {
      icon: Search,
      title: t('home.compFinder'),
      description: 'AI-powered comparable sales search with automatic property matching.',
      link: '/comps'
    },
    {
      icon: Wrench,
      title: t('home.repairEstimator'),
      description: 'Get accurate repair cost estimates based on property condition and local data.',
      link: '/repair-estimator'
    },
    {
      icon: MessageSquare,
      title: t('home.aiAssistant'),
      description: 'Chat with AI to analyze properties, get market insights, and make decisions.',
      link: '/chat'
    },
    {
      icon: FileText,
      title: t('home.proReports'),
      description: 'Generate professional PDF reports for lenders, partners, and buyers.',
      link: '/dashboard'
    },
    {
      icon: TrendingUp,
      title: t('home.dealMarketing'),
      description: 'Create marketing materials to sell wholesale deals and build buyers lists.',
      link: '/dashboard'
    }
  ];

  const strategies = [
    { name: 'Wholesale', color: 'bg-purple-500' },
    { name: 'Fix & Flip', color: 'bg-pink-500' },
    { name: 'BRRRR', color: 'bg-fuchsia-500' },
    { name: 'Buy & Hold', color: 'bg-violet-500' },
    { name: 'Commercial', color: 'bg-rose-500' },
    { name: 'New Construction', color: 'bg-purple-600' }
  ];

  const stats = [
    { value: '$2.5B+', label: t('home.dealsAnalyzed') },
    { value: '98%', label: t('home.accuracy') },
    { value: '< 30s', label: t('home.analysisTime') },
    { value: '6', label: t('home.exitStrategies') }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEO 
        title={t('home.propertyarvRealEstateInvestmentAnalysis')}
        description="Analyze real estate deals in seconds. Calculate ARV, estimate repairs, find comps, and determine ROI instantly. Free tool for flippers, wholesalers, and rental investors."
        keywords="real estate investing, ARV calculator, property analysis, fix and flip, wholesale real estate, rental property calculator, rehab estimator, deal analyzer"
      />
      {/* Hero Section */}
      <section className="relative pt-20 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-purple-100/50 dark:bg-pink-900/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-pink-100/30 dark:bg-pink-900/10 rounded-full blur-3xl" />
        
        {/* Promo Banner - inside hero section for proper layering */}
        <div className="relative z-10">
          <PromoBanner />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-pink-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 border-0 px-4 py-1.5">
                  <Zap className="w-3 h-3 mr-1" />
                  {t('home.badge')}
                </Badge>
                <CountdownTimer />
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                {t('home.title')}{' '}
                <span className="relative">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">{t('home.titleHighlight')}</span>
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                    <path d="M2 8C50 3 150 3 198 8" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round" />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#EC4899" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-lg">
                {t('home.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-xl shadow-purple-500/25 hover:shadow-purple-500/40 transition-all text-lg px-8 py-6"
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate('/analyzer');
                    } else {
                      toast.info('Please sign in to access features');
                      navigate('/login');
                    }
                  }}
                  data-testid="start-analysis-btn"
                >
                  {t('home.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              
              <p className="text-sm text-gray-500">{t('home.ctaSubtext')}</p>
            </div>

            {/* Right Content - Product Video */}
            <div className="relative lg:ml-8">
              <div className="relative rounded-2xl shadow-2xl border border-purple-100 dark:border-purple-800 overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="w-full rounded-2xl"
                  data-testid="hero-video"
                  ref={(el) => {
                    if (el) {
                      el.play().then(() => {
                        el.muted = false;
                      }).catch(() => {});
                    }
                  }}
                >
                  <source src="https://customer-assets.emergentagent.com/job_88b20312-eee5-4cdc-910d-99810735713c/artifacts/ibhhyw0z_PROPERTYARV%20%281%29.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Platform Stats */}
      <section className="py-6 bg-gray-50/50 dark:bg-gray-800/30 border-y border-gray-100 dark:border-gray-800" data-testid="live-stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <LiveStatsBar />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-purple-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Detailed Showcase */}
      <section className="py-24 bg-white dark:bg-gray-900" data-testid="features-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-pink-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 border-0 mb-4">{t('home.platformFeatures')}</Badge>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('home.featuresTitle')}</h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('home.featuresSubtitle')}
            </p>
          </div>

          {/* Feature 1: Deal Analyzer */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-showcase-0">
            <div className="space-y-5 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                <Calculator className="w-4 h-4 text-purple-600 dark:text-pink-400" />
                <span className="text-sm font-medium text-purple-700 dark:text-pink-400">Deal Analyzer</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                Analyze any deal in under 30 seconds
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Run instant analysis on wholesale, fix-and-flip, BRRRR, buy-and-hold, commercial, and new construction deals. Get ARV, MAO, ROI, cash flow, and cap rate automatically calculated.
              </p>
              <ul className="space-y-2.5">
                {['6 investment strategies in one tool', 'Auto-populated comps & market data', 'Professional PDF reports for lenders'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white mt-2"
                onClick={() => isAuthenticated ? navigate('/analyzer') : navigate('/login')}
                data-testid="feature-cta-analyzer"
              >
                Try Deal Analyzer <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-purple-500/10 border border-gray-200 dark:border-gray-700 group">
                <img
                  src="/screenshots/deal-analyzer.png"
                  alt="Deal Analyzer Dashboard"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Feature 2: Comp Finder */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-showcase-1">
            <div className="order-1">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-purple-500/10 border border-gray-200 dark:border-gray-700 group">
                <img
                  src="/screenshots/comp-finder.png"
                  alt="Comp Finder Tool"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
            <div className="space-y-5 order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                <Search className="w-4 h-4 text-purple-600 dark:text-pink-400" />
                <span className="text-sm font-medium text-purple-700 dark:text-pink-400">Comp Finder</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                Find comps and calculate ARV instantly
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Search comparable sales by address, filter by beds, baths, sqft, and year built. Sort results, auto-select the best matches, and compare side-by-side with your subject property.
              </p>
              <ul className="space-y-2.5">
                {['Sort by price, sqft, distance, or date', 'Side-by-side comparison table', 'Section 8 Fair Market Rent lookup'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white mt-2"
                onClick={() => isAuthenticated ? navigate('/comp-finder') : navigate('/login')}
                data-testid="feature-cta-compfinder"
              >
                Try Comp Finder <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>

          {/* Feature 3: Repair Estimator */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-showcase-2">
            <div className="space-y-5 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                <Wrench className="w-4 h-4 text-purple-600 dark:text-pink-400" />
                <span className="text-sm font-medium text-purple-700 dark:text-pink-400">Repair Estimator</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                Accurate rehab cost breakdowns
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Itemize repair costs by category with separate labor and material estimates. Generate professional Statements of Work and share them via unique links.
              </p>
              <ul className="space-y-2.5">
                {['Itemized labor & material costs', 'Shareable SOW with branding', 'Export to professional PDF'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white mt-2"
                onClick={() => isAuthenticated ? navigate('/repair-estimator') : navigate('/login')}
                data-testid="feature-cta-repair"
              >
                Try Repair Estimator <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-purple-500/10 border border-gray-200 dark:border-gray-700 group">
                <img
                  src="/screenshots/repair-estimator.png"
                  alt="Repair Estimator Tool"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Feature 4: AI Assistant */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-showcase-3">
            <div className="order-1">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-purple-500/10 border border-gray-200 dark:border-gray-700 group">
                <img
                  src="/screenshots/ai-assistant.png"
                  alt="AI Chat Assistant"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
            <div className="space-y-5 order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                <MessageSquare className="w-4 h-4 text-purple-600 dark:text-pink-400" />
                <span className="text-sm font-medium text-purple-700 dark:text-pink-400">AI Assistant</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                Your personal real estate analyst
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Chat with AI to analyze properties, get market insights, compare strategies, and make data-driven investment decisions faster than ever.
              </p>
              <ul className="space-y-2.5">
                {['Natural language property analysis', 'Market insights & trend data', 'Strategy recommendations on demand'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white mt-2"
                onClick={() => isAuthenticated ? navigate('/chat') : navigate('/login')}
                data-testid="feature-cta-ai"
              >
                Try AI Assistant <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>

          {/* Feature 5: E-Sign */}
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-testid="feature-showcase-4">
            <div className="space-y-5 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-100 dark:bg-pink-900/40 rounded-lg">
                <FileSignature className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                <span className="text-sm font-medium text-pink-700 dark:text-pink-400">E-Sign</span>
                <Badge className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 border-0 text-[10px] px-1.5 py-0">NEW</Badge>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                DocuSign-style signing, built right in
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Upload contracts, drag and drop signature fields, send for signing via email, and download completed documents — no external signing tools needed.
              </p>
              <ul className="space-y-2.5">
                {['Drag & drop field placement on any PDF', 'Multi-signer support with email notifications', 'Legally binding e-signatures with tracking'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white mt-2"
                onClick={() => isAuthenticated ? navigate('/esign') : navigate('/login')}
                data-testid="feature-cta-esign"
              >
                Try E-Sign <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-purple-500/10 border border-gray-200 dark:border-gray-700 group">
                <img
                  src="/screenshots/esign.png"
                  alt="E-Sign Electronic Signatures"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* More Platform Tools - Screenshot Grid */}
          <div className="mt-24" data-testid="features-grid">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Everything you need to run your real estate business
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                From lead tracking to closing — all in one platform
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: FolderKanban, title: 'Project Manager', desc: 'Track rehab progress, tasks, and timelines for every deal', img: '/screenshots/project-manager.png', route: '/projects' },
                { icon: Users, title: 'CRM', desc: 'Manage contacts, leads, buyers, and follow-ups', img: '/screenshots/crm.png', route: '/crm' },
                { icon: Megaphone, title: 'Deal Marketing', desc: 'Create listing pages and marketing materials for your deals', img: '/screenshots/deal-marketing.png', route: '/marketing' },
                { icon: HardHat, title: 'Contractor Directory', desc: 'Find and manage trusted contractors for your rehabs', img: '/screenshots/contractors.png', route: '/contractors' },
                { icon: Store, title: 'Wholesale Hub', desc: 'List deals, build buyer lists, and close wholesale assignments', img: '/screenshots/wholesale-hub.png', route: '/wholesale' },
                { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Portfolio performance, ROI tracking, and market insights', img: '/screenshots/analytics.png', route: '/analytics' },
                { icon: FileSignature, title: 'Contract Templates', desc: 'Pre-built contracts for assignments, purchases, and JVs', img: '/screenshots/contracts.png', route: '/contracts' },
                { icon: Landmark, title: 'Funding Proposals', desc: 'Generate professional funding proposals for lenders', img: '/screenshots/funding.png', route: '/funding' },
              ].map((feature, i) => (
                <Card
                  key={i}
                  className="group border-gray-100 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => isAuthenticated ? navigate(feature.route) : navigate('/login')}
                  data-testid={`feature-grid-card-${i}`}
                >
                  <div className="relative h-36 overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <img
                      src={feature.img}
                      alt={feature.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-2 left-2 w-8 h-8 bg-white/90 dark:bg-gray-900/90 backdrop-blur rounded-lg flex items-center justify-center">
                      <feature.icon className="w-4 h-4 text-purple-600 dark:text-pink-400" />
                    </div>
                    {feature.isNew && (
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-green-500 text-white border-0 text-[10px] px-1.5 py-0">NEW</Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors text-sm">
                      {feature.title}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Business Directory Banner */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-purple-950 to-gray-900 relative overflow-hidden" data-testid="directory-banner">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-purple-500 blur-[100px]" />
          <div className="absolute bottom-10 right-10 w-64 h-64 rounded-full bg-pink-500 blur-[100px]" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 mb-4 text-xs">
              Business Directory
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Get Found by Real Estate Investors
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              List your business in PropertyARV's directory and connect with thousands of active real estate investors nationwide
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: Building2, title: 'Real Estate Agents', desc: 'Buyers agents, listing agents, investment specialists', color: 'from-blue-500 to-blue-600' },
              { icon: HardHat, title: 'Contractors', desc: 'General contractors, roofers, electricians, plumbers', color: 'from-orange-500 to-orange-600' },
              { icon: Landmark, title: 'Lenders', desc: 'Hard money, private money, conventional lenders', color: 'from-green-500 to-green-600' },
              { icon: Store, title: 'Wholesalers', desc: 'Wholesale deal finders, assignment specialists', color: 'from-purple-500 to-purple-600' },
            ].map((item, i) => (
              <div
                key={i}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 hover:border-purple-500/40 transition-all cursor-pointer"
                onClick={() => navigate('/directory/register')}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-base shadow-lg shadow-purple-500/25"
              onClick={() => navigate('/directory/register')}
              data-testid="directory-banner-register-btn"
            >
              List Your Business — It's Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-base"
              onClick={() => navigate('/directory')}
              data-testid="directory-banner-browse-btn"
            >
              Browse Directory
            </Button>
          </div>

          <p className="text-center text-gray-500 text-xs mt-4">
            Free listings available — upgrade to $39/mo for full profile visibility
          </p>
        </div>
      </section>

      {/* Investment Strategies */}
      <section className="py-24 bg-purple-50 dark:bg-purple-950/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('home.analyzeEveryExitStrategy')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">{t('home.onePlatformForAllYour')}</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {strategies.map((strategy, index) => (
              <div
                key={index}
                className="flex items-center gap-3 bg-white dark:bg-gray-800 px-6 py-4 rounded-xl shadow-sm border border-purple-100 dark:border-purple-900/50 hover:shadow-md hover:border-purple-200 dark:hover:border-purple-700 transition-all cursor-pointer"
              >
                <div className={`w-3 h-3 rounded-full ${strategy.color}`} />
                <span className="font-medium text-gray-700 dark:text-gray-300">{strategy.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/50 border-0 mb-4">{t('home.testimonials')}</Badge>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('home.trustedByInvestorsNationwide')}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="relative border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-white to-purple-50/50 dark:from-gray-800 dark:to-purple-950/20 overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
                <CardContent className="p-6 pt-7">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed italic">"{testimonial.content}"</p>
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-purple-200 dark:ring-purple-800"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{testimonial.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-purple-100 dark:border-purple-900/50">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold text-purple-600 dark:text-pink-400">{testimonial.deals}</span> deals analyzed
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop wasting hours analyzing deals
          </h2>
          <p className="text-xl text-purple-200 mb-10 max-w-2xl mx-auto">
            Join thousands of investors who close deals faster with instant property analysis.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white shadow-xl shadow-pink-500/25 text-lg px-8 py-6"
              onClick={() => {
                if (isAuthenticated) {
                  navigate('/analyzer');
                } else {
                  toast.info('Please sign in to access features');
                  navigate('/login');
                }
              }}
              data-testid="cta-start-analysis-btn"
            >
              Start Free Analysis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 text-purple-300">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              Free 7-day trial
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-pink-400" />
              Setup in minutes
            </span>
          </div>
        </div>
      </section>
      {/* Admin Access Section */}
      <section className="py-8 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-gray-400">
              <Shield className="w-5 h-5" />
              <span className="text-sm">{t('home.propertyarvAdminPortal')}</span>
            </div>
            {isAuthenticated && user?.isAdmin ? (
              <Link to="/admin">
                <Button variant="outline" className="border-purple-500 text-purple-400 hover:bg-purple-500/10">
                  <Shield className="w-4 h-4 mr-2" />
                  Go to Admin Dashboard
                </Button>
              </Link>
            ) : (
              <Button 
                variant="outline" 
                className="border-gray-700 text-gray-400 hover:border-purple-500 hover:text-purple-400 hover:bg-purple-500/10"
                onClick={() => setShowAdminLogin(true)}
                data-testid="admin-login-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Admin Login
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Admin Login Dialog */}
      <Dialog open={showAdminLogin} onOpenChange={setShowAdminLogin}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle>{t('home.adminLogin')}</DialogTitle>
                <DialogDescription>
                  Access the PropertyARV admin dashboard
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <form onSubmit={handleAdminLogin} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email">{t('home.email')}</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="Enter admin email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                className="border-purple-200 focus:border-purple-400"
                data-testid="admin-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-password">{t('home.password')}</Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="••••••••"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                required
                className="border-purple-200 focus:border-purple-400"
                data-testid="admin-password-input"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white"
              disabled={isLoading}
              data-testid="admin-login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Sign In as Admin
                </>
              )}
            </Button>
            
            <p className="text-xs text-center text-gray-500 mt-4">
              Admin access is restricted to authorized personnel only.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
